import { useMemo, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Copy,
  ExternalLink,
  FileCheck2,
  Flag,
  RotateCcw,
  Route,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import SEO from "@/components/SEO";
import type { CompetencyCourseDefinition } from "@/components/training/CompetencyCourse";
import type { GuideAction, GuideLink, GuidePrompt } from "@/components/training/SlideGuide";
import { AI_BUILDER_COURSE_PACKAGE } from "@/data/aiBuilderPlaybook";
import { CLAUDE_BASICS_STEPS } from "@/data/claudeBasics";
import { MODULES } from "@/data/claudeMasterclass";
import {
  decisionMakingCourse,
  responsibleAIPracticeCourse,
  ricePrioritiesCourse,
  tdarResultsCourse,
} from "@/data/longhandCompetencyCourses";
import {
  LONGHAND_EVIDENCE_MILESTONES,
  LONGHAND_PLATFORM_PATHS,
  LONGHAND_PROGRAM_ATLAS,
  TWO_AGENT_HANDOFF_LAB,
} from "@/data/longhandAIMegaGuide";
import { S2P_ELEVATE_PROGRAM, type S2PElevateSlide } from "@/data/s2pElevateAI";
import {
  LONGHAND_GUIDE_SCREENSHOTS,
  LONGHAND_GUIDE_SESSIONS,
  type LonghandGuideStep,
} from "@/data/longhandAIProgramGuide";
import "./LonghandAIProgram.css";

const copyText = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Permission-restricted browsers may expose clipboard without allowing writes.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

const toAtlasLanguage = (value: string) => value
  .replace("Median minutes from source close to approved incident report", "Median preparation effort from source close to approved incident report")
  .replace("Reduce median preparation time for the weekly approved report from 150 to 75 minutes while keeping material post-review corrections at or below the baseline.", "Reduce median preparation effort by 50% while keeping material post-review corrections at or below the baseline.")
  .replace("Eight weekly reports; median 150 minutes; three contained a material post-review correction. Source: timestamped report history.", "Eight weekly reports; preparation-effort baseline recorded; three contained a material post-review correction. Source: timestamped report history.")
  .replace("Median preparation is 150 minutes across eight reports; test whether drafting time can fall without increasing material corrections.", "Preparation effort is consistently high across eight reports; test whether drafting effort can fall without increasing material corrections.")
  .replace("Median review exceeds 45 minutes", "Median review effort exceeds the agreed threshold")
  .replace("Preparation baseline is 150 median minutes; test time reduction without increasing corrections.", "The preparation baseline is documented; test effort reduction without increasing corrections.")
  .replace(/\bSessions ([1-5])(?:–|-)([1-5])\b/g, "Capabilities $1–$2")
  .replace(/\bSession ([1-5])\b/g, "Capability $1")
  .replace(/\bthe next four sessions\b/gi, "the next four capabilities")
  .replace(/\blive-session\b/gi, "live demonstration")
  .replace(/\ban approved session\b/gi, "an approved account");

const PromptBlock = ({
  slide,
  copiedId,
  onCopy,
}: {
  slide: S2PElevateSlide;
  copiedId: string | null;
  onCopy: (id: string, prompt: string) => void;
}) => {
  if (!slide.prompt) return null;

  const copyId = `prompt-${slide.id}`;
  const copied = copiedId === copyId;

  return (
    <section className="lhp-prompt" aria-labelledby={`${copyId}-title`}>
      <div className="lhp-prompt-head">
        <div>
          <p>Copy and customize</p>
          <h4 id={`${copyId}-title`}>{toAtlasLanguage(slide.promptLabel ?? slide.title)}</h4>
        </div>
        <button type="button" onClick={() => onCopy(copyId, slide.prompt!)} aria-live="polite">
          {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copied ? "Copied" : "Copy prompt"}
        </button>
      </div>
      <pre><code>{slide.prompt}</code></pre>
    </section>
  );
};

const ScreenshotGallery = ({ screenshotIds }: { screenshotIds: string[] }) => (
  <div className={`lhp-screenshot-grid ${screenshotIds.length > 1 ? "is-paired" : ""}`}>
    {screenshotIds.map((screenshotId) => {
      const screenshot = LONGHAND_GUIDE_SCREENSHOTS[screenshotId];
      if (!screenshot) return null;

      return (
        <figure key={screenshot.id} className="lhp-screenshot">
          <a href={screenshot.src} target="_blank" rel="noopener noreferrer" aria-label={`Open full-size screenshot: ${screenshot.alt}`}>
            <img
              src={screenshot.src}
              width={screenshot.width}
              height={screenshot.height}
              alt={screenshot.alt}
              loading="lazy"
              decoding="async"
            />
          </a>
          <figcaption>{screenshot.caption} <span>Open the image to inspect the full-size capture.</span></figcaption>
        </figure>
      );
    })}
  </div>
);

interface MegaGuideStep {
  id: string;
  title: string;
  description: string;
  actions: GuideAction[];
  checkpoint: string;
  prompts?: GuidePrompt[];
  deliverables?: string[];
  image?: { src: string; alt: string; caption?: string; width?: number; height?: number };
  links?: GuideLink[];
  codeBlock?: { caption?: string; code: string };
}

const LabPromptBlock = ({
  id,
  label,
  value,
  copiedId,
  onCopy,
}: {
  id: string;
  label: string;
  value: string;
  copiedId: string | null;
  onCopy: (id: string, prompt: string) => void;
}) => {
  const copied = copiedId === id;

  return (
    <section className="lhp-prompt lhp-lab-prompt" aria-labelledby={`${id}-title`}>
      <div className="lhp-prompt-head">
        <div>
          <p>Copy and customize</p>
          <h4 id={`${id}-title`}>{label}</h4>
        </div>
        <button type="button" onClick={() => onCopy(id, value)} aria-live="polite">
          {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copied ? "Copied" : "Copy prompt"}
        </button>
      </div>
      <pre><code>{value}</code></pre>
    </section>
  );
};

const MegaGuideStepCard = ({
  step,
  number,
  copiedId,
  onCopy,
}: {
  step: MegaGuideStep;
  number: number;
  copiedId: string | null;
  onCopy: (id: string, prompt: string) => void;
}) => (
  <article className="lhp-lab-step" id={step.id}>
    <header>
      <span>{String(number).padStart(2, "0")}</span>
      <div>
        <p>Step {number}</p>
        <h4>{step.title}</h4>
      </div>
    </header>

    {step.image && (
      <figure className="lhp-lab-image">
        <a href={step.image.src} target="_blank" rel="noopener noreferrer" aria-label={`Open full-size screenshot: ${step.image.alt}`}>
          <img src={step.image.src} width={step.image.width} height={step.image.height} alt={step.image.alt} loading="lazy" decoding="async" />
        </a>
        {step.image.caption && <figcaption>{step.image.caption}</figcaption>}
      </figure>
    )}

    <p className="lhp-lab-label">Do this</p>
    <ol className="lhp-lab-actions">
      {step.actions.map((action, index) => (
        <li key={typeof action === "string" ? action : action.text}>
          <span>{index + 1}</span>
          <div>
            <p>{typeof action === "string" ? action : action.text}</p>
            {typeof action !== "string" && <ul>{action.subSteps.map((item) => <li key={item}>{item}</li>)}</ul>}
          </div>
        </li>
      ))}
    </ol>

    {step.prompts?.map((prompt, index) => (
      <LabPromptBlock
        key={`${step.id}-${prompt.label}`}
        id={`lab-prompt-${step.id}-${index}`}
        label={prompt.label}
        value={prompt.value}
        copiedId={copiedId}
        onCopy={onCopy}
      />
    ))}

    {step.codeBlock && (
      <LabPromptBlock
        id={`lab-code-${step.id}`}
        label={step.codeBlock.caption ?? step.title}
        value={step.codeBlock.code}
        copiedId={copiedId}
        onCopy={onCopy}
      />
    )}

    {step.deliverables && (
      <div className="lhp-lab-deliverables">
        <strong>Leave with</strong>
        <ul>{step.deliverables.map((item) => <li key={item}>{item}</li>)}</ul>
      </div>
    )}

    {step.links && (
      <div className="lhp-lab-links">
        {step.links.map((link) => {
          const external = link.external ?? /^https?:\/\//.test(link.href);
          return <a key={link.href} href={link.href} download={link.download} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>{link.label}<ExternalLink aria-hidden="true" /></a>;
        })}
      </div>
    )}

    <footer><CheckCircle2 aria-hidden="true" /><p><strong>Done when</strong>{step.checkpoint}</p></footer>
  </article>
);

const DeepDiveChapter = ({
  id,
  eyebrow,
  title,
  summary,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  summary: string;
  children: ReactNode;
}) => (
  <section className="lhp-mega-chapter" id={id} aria-labelledby={`${id}-title`}>
    <div className="lhp-mega-chapter-intro">
      <p className="lhp-eyebrow">{eyebrow}</p>
      <h2 id={`${id}-title`}>{title}</h2>
    </div>
    <details className="lhp-mega-disclosure">
      <summary>
        <span><Sparkles aria-hidden="true" /><span><strong>{summary}</strong></span></span>
        <ChevronDown aria-hidden="true" />
      </summary>
      <div className="lhp-mega-body">{children}</div>
    </details>
  </section>
);

const CourseLab = ({
  course,
  copiedId,
  onCopy,
}: {
  course: CompetencyCourseDefinition;
  copiedId: string | null;
  onCopy: (id: string, prompt: string) => void;
}) => {
  const howToSteps = course.steps.filter((step) => !["worked-tdar", "worked-dmp", "rice-worked-example"].includes(step.id));

  return (
    <section className="lhp-course-lab" aria-labelledby={`${course.id}-lab-title`}>
      <header>
        <div>
          <p>{howToSteps.length} steps</p>
          <h3 id={`${course.id}-lab-title`}>{course.name}</h3>
        </div>
        <a href={course.canonicalPath}>Open standalone course <ArrowRight aria-hidden="true" /></a>
      </header>
      <div className="lhp-lab-step-list">
        {howToSteps.map((step, index) => (
          <MegaGuideStepCard key={step.id} step={step} number={index + 1} copiedId={copiedId} onCopy={onCopy} />
        ))}
      </div>
      <aside className="lhp-course-evidence">
        <ClipboardCheck aria-hidden="true" />
        <div><strong>Save this evidence</strong><p>{course.assessment.evidence.join(" · ")}</p></div>
      </aside>
    </section>
  );
};

const guideScreenshotBySrc = new Map(Object.values(LONGHAND_GUIDE_SCREENSHOTS).map((screenshot) => [screenshot.src, screenshot]));

const FOUNDATION_STEPS: MegaGuideStep[] = CLAUDE_BASICS_STEPS
  .filter((step) => ["work-from-file", "create-artifact", "save-workflow"].includes(step.id))
  .map((step) => {
    const screenshot = step.image ? guideScreenshotBySrc.get(step.image.src) : undefined;
    return {
      ...step,
      image: step.image ? { ...step.image, width: screenshot?.width, height: screenshot?.height } : undefined,
      links: step.links?.map((link) => ({ ...link, external: /^https?:\/\//.test(link.href) })),
    };
  });

const skillForgeModule = MODULES.find((module) => module.id === "module-5");

const SKILL_FORGE_STEPS: MegaGuideStep[] = (skillForgeModule?.steps ?? []).slice(2).map((step, index) => ({
  id: `skill-forge-step-${index + 1}`,
  title: step.title,
  description: step.description,
  actions: step.actionSteps,
  checkpoint: step.checkpoint,
  image: step.image ?? (index === 0 ? LONGHAND_GUIDE_SCREENSHOTS["skills-library"] : undefined),
  codeBlock: step.codeBlock,
  links: [
    ...(step.download ? [{ label: step.download.label, href: step.download.href, download: true }] : []),
    ...(step.links ?? []).map((link) => ({ label: link.label, href: link.href, external: /^https?:\/\//.test(link.href) })),
  ],
}));

const ProgramAtlas = () => (
  <section className="lhp-atlas" id="program-atlas" aria-labelledby="program-atlas-title">
    <div className="lhp-atlas-head">
      <div>
        <p className="lhp-eyebrow">Choose your next how-to</p>
        <h2 id="program-atlas-title">Choose one red or yellow result.</h2>
      </div>
      <p>Use your first red or yellow rating to choose the closest how-to below. Complete the checks, then retake the AI Tune-Up.</p>
    </div>
    <ol>
      {LONGHAND_PROGRAM_ATLAS.map((chapter) => (
        <li key={chapter.number} data-kind={chapter.kind.toLowerCase().replace(" ", "-")}>
          <a href={chapter.href}>
            <span>{chapter.number}</span>
            <div><p>{chapter.kind}</p><h3>{chapter.title}</h3><small>{chapter.outcome}</small></div>
            <ArrowRight aria-hidden="true" />
          </a>
        </li>
      ))}
    </ol>
  </section>
);

const PlatformPaths = () => (
  <div className="lhp-platform-grid">
    {LONGHAND_PLATFORM_PATHS.map((path) => (
      <article key={path.id} id={path.id}>
        <header><Route aria-hidden="true" /><p>{path.label} path</p><h3>{path.title}</h3></header>
        <dl><dt>Choose this when</dt><dd>{path.useWhen}</dd></dl>
        {path.id === "claude-path" && <ScreenshotGallery screenshotIds={["project-library", "skills-features", "agent-workspace"]} />}
        {path.id === "builder-path" && <ScreenshotGallery screenshotIds={["code-app", "code-safety"]} />}
        <p className="lhp-lab-label">Do this</p>
        <ol>{path.sequence.map((item) => <li key={item}>{item}</li>)}</ol>
        <p className="lhp-platform-stop"><Flag aria-hidden="true" /><span><strong>Stop rule</strong>{path.stopRule}</span></p>
        <div>{path.links.map((link) => <a href={link.href} key={link.href}>{link.label}<ArrowRight aria-hidden="true" /></a>)}</div>
      </article>
    ))}
  </div>
);

const BuilderPath = () => (
  <section className="lhp-builder-path" aria-labelledby="builder-path-title">
    <header>
      <div><p>Advanced build path</p><h3 id="builder-path-title">{AI_BUILDER_COURSE_PACKAGE.title}</h3></div>
      <a href="/ai-builder-playbook">Open the full playbook <ArrowRight aria-hidden="true" /></a>
    </header>
    <ScreenshotGallery screenshotIds={["code-download", "code-app", "code-safety"]} />
    <ol>
      {AI_BUILDER_COURSE_PACKAGE.modules.map((module, index) => {
        const outcomes = AI_BUILDER_COURSE_PACKAGE.outcomes.filter((outcome) => (module.outcomes as readonly string[]).includes(outcome.id));
        return (
          <li key={module.id}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div><h4>{module.title}</h4><ul>{outcomes.map((outcome) => <li key={outcome.id}>{outcome.statement}</li>)}</ul></div>
          </li>
        );
      })}
    </ol>
  </section>
);

const AgentHandoffLab = () => (
  <section className="lhp-handoff-lab" id="two-agent-handoff" aria-labelledby="two-agent-handoff-title">
    <header><Users aria-hidden="true" /><div><p>Optional multi-agent steps</p><h3 id="two-agent-handoff-title">Coordinate two agents with one human owner.</h3></div></header>
    <ScreenshotGallery screenshotIds={["agent-workspace"]} />
    <ol>
      {TWO_AGENT_HANDOFF_LAB.map((step, index) => (
        <li key={step.title}><span>{index + 1}</span><div><h4>{step.title}</h4><p>{step.action}</p><small><strong>Keep:</strong> {step.evidence}</small></div></li>
      ))}
    </ol>
    <p className="lhp-handoff-check"><CheckCircle2 aria-hidden="true" /><span><strong>Done means</strong>You have the original artifact, an independent review, the disagreements, the human decision, and one change to the next handoff.</span></p>
  </section>
);

const EvidencePortfolio = () => (
  <section className="lhp-evidence-portfolio" id="evidence-portfolio" aria-labelledby="evidence-portfolio-title">
    <div className="lhp-evidence-head">
      <div><p className="lhp-eyebrow">Save your work</p><h2 id="evidence-portfolio-title">Keep these four evidence packages.</h2></div>
      <p>Save each package in the same Chief of Staff workspace.</p>
    </div>
    <ol>
      {LONGHAND_EVIDENCE_MILESTONES.map((milestone) => (
          <li key={milestone.id} id={`milestone-${milestone.id}`}>
            <header><span>{milestone.number}</span><div><p>Save</p><h3>{milestone.title}</h3></div></header>
            <ul>{milestone.evidence.map((item) => <li key={item}>{item}</li>)}</ul>
            <footer><ClipboardCheck aria-hidden="true" /><p><strong>Check with your reviewer</strong>{milestone.reviewQuestion}</p></footer>
          </li>
      ))}
    </ol>
  </section>
);

const GuideStep = ({
  step,
  number,
  slidesById,
  copiedId,
  onCopy,
}: {
  step: LonghandGuideStep;
  number: number;
  slidesById: Map<string, S2PElevateSlide>;
  copiedId: string | null;
  onCopy: (id: string, prompt: string) => void;
}) => {
  const sourceSlide = slidesById.get(step.id);
  const promptSlides = (step.promptSlideIds ?? [])
    .map((id) => slidesById.get(id))
    .filter((slide): slide is S2PElevateSlide => Boolean(slide?.prompt));

  return (
    <article className="lhp-step" id={step.id}>
      {step.aliases?.map((alias) => <span className="lhp-alias-anchor" id={alias} key={alias} aria-hidden="true" />)}
      <div className="lhp-step-rail" aria-hidden="true">
        <span>{String(number).padStart(2, "0")}</span>
        <i />
      </div>
      <div className="lhp-step-content">
        <p className="lhp-kicker">{step.kicker}</p>
        <h3>{sourceSlide?.title ?? step.instruction}</h3>
        <p className="lhp-instruction">{step.instruction}</p>

        {step.screenshotIds && <ScreenshotGallery screenshotIds={step.screenshotIds} />}

        <section className="lhp-actions" aria-labelledby={`${step.id}-actions`}>
          <h4 id={`${step.id}-actions`}>Do this</h4>
          <ol>
            {step.actions.map((action, index) => (
              <li key={action}><span>{index + 1}</span><p>{action}</p></li>
            ))}
          </ol>
        </section>

        {promptSlides.map((slide) => (
          <PromptBlock key={slide.id} slide={slide} copiedId={copiedId} onCopy={onCopy} />
        ))}

        <div className="lhp-result-grid">
          <section className="lhp-expected" aria-labelledby={`${step.id}-expected`}>
            <FileCheck2 aria-hidden="true" />
            <div>
              <h4 id={`${step.id}-expected`}>What you should see</h4>
              <p>{step.expected}</p>
            </div>
          </section>
          <section className="lhp-verify" aria-labelledby={`${step.id}-verify`}>
            <ClipboardCheck aria-hidden="true" />
            <div>
              <h4 id={`${step.id}-verify`}>Check before moving on</h4>
              <ul>{step.verify.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          </section>
        </div>

        {step.stop && <p className="lhp-step-stop"><Flag aria-hidden="true" /><span><strong>Stop rule:</strong> {step.stop}</span></p>}

        <details className="lhp-recovery">
          <summary><span><RotateCcw aria-hidden="true" /> If this step fails</span><ChevronDown aria-hidden="true" /></summary>
          <ul>{step.recover.map((item) => <li key={item}>{item}</li>)}</ul>
        </details>
      </div>
    </article>
  );
};

const LonghandAIProgram = () => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const slidesById = useMemo(
    () => new Map(S2P_ELEVATE_PROGRAM.slides.map((slide) => [slide.id, slide])),
    [],
  );
  const representedIds = useMemo(() => new Set([
    "welcome",
    "use-case-library-workflows",
    "use-case-library-scale",
    ...S2P_ELEVATE_PROGRAM.sessions.map((session) => `session-${session.id}`),
    ...LONGHAND_GUIDE_SESSIONS.flatMap((session) => session.steps.flatMap((step) => [step.id, ...(step.aliases ?? [])])),
  ]), []);

  const handleCopy = async (id: string, prompt: string) => {
    await copyText(prompt);
    setCopiedId(id);
    window.setTimeout(() => setCopiedId((current) => current === id ? null : current), 1800);
  };

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: "Longhand AI Complete Program Atlas",
    description: "The self-guided Longhand AI program atlas: use the AI Tune-Up to choose core capabilities, deeper decision and platform labs, controlled agent work, Skill Forge, Safety & Governance, and evidence milestones.",
    url: "https://www.technical-leaders.com/ai-30/program/atlas",
    provider: {
      "@type": "Organization",
      name: "Technical Leaders",
      url: "https://www.technical-leaders.com",
    },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "online",
    },
  };

  return (
    <div className="lhp-page">
      <SEO
        title="Longhand AI Atlas: Complete Program Guide"
        description="Use the AI Tune-Up to navigate a self-guided Longhand AI atlas of practical capabilities, deeper labs, platform paths, agent controls, Skills, governance, and evidence."
        keywords={["Longhand AI", "AI training for leaders", "Chief of Staff agent", "AI prompting", "AI tool selection", "AI agent governance", "AI improvement loop"]}
        structuredData={courseSchema}
      />

      <a className="lhp-skip" href="#program-guide">Skip to the guide</a>

      <header className="lhp-site-header">
        <a href="/ai-30/program" className="lhp-brand" aria-label="Back to the Longhand AI roadmap">
          <img src="/orange-logo.png" alt="Tech Leaders" width="170" height="38" />
        </a>
        <nav aria-label="Atlas navigation">
          <a href="/ai-30/program"><ArrowLeft aria-hidden="true" /> Roadmap</a>
          <a href="#program-atlas">Program atlas</a>
          <div className="lhp-session-links">
            {S2P_ELEVATE_PROGRAM.sessions.map((session) => (
              <a key={session.id} href={`#session-${session.id}`} aria-label={`Capability ${session.id}: ${session.title}`}>{session.id}</a>
            ))}
          </div>
          <a href="/ai-30/program#ai-tune-up">AI Tune-Up</a>
        </nav>
      </header>

      <main id="program-guide">
        <section className="lhp-atlas-hero" id="welcome" aria-labelledby="program-title">
          <h1 id="program-title">Longhand AI Atlas</h1>
          <p>The step-by-step companion to your AI Tune-Up and personalized AI Roadmap. Start with the gaps you identified, then follow the matching how-to guides.</p>
        </section>

        <ProgramAtlas />

        <nav className="lhp-journey-nav" aria-label="Core capability path">
          <ol>
            {S2P_ELEVATE_PROGRAM.sessions.map((session) => (
              <li key={session.id} data-session={session.id}>
                <a href={`#session-${session.id}`}>
                  <span>{session.id}</span>
                  <strong>{session.title}</strong>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {LONGHAND_GUIDE_SESSIONS.map((guideSession) => {
          const session = S2P_ELEVATE_PROGRAM.sessions.find((candidate) => candidate.id === guideSession.id)!;
          const legacyAnchors = S2P_ELEVATE_PROGRAM.slides.filter(
            (slide) => slide.session === session.id && !representedIds.has(slide.id),
          );

          return (
            <section className="lhp-session" id={`session-${session.id}`} data-session={session.id} key={session.id} aria-labelledby={`session-${session.id}-title`}>
              <div className="lhp-anchor-bundle" aria-hidden="true">
                {legacyAnchors.map((slide) => <span id={slide.id} key={slide.id} />)}
              </div>
              <header className="lhp-session-head">
                <div className="lhp-session-number" aria-hidden="true">0{session.id}</div>
                <div>
                  <p className="lhp-eyebrow">Core capability {session.id} of 5</p>
                  <h2 id={`session-${session.id}-title`}>{session.title}</h2>
                </div>
                <dl>
                  <div><dt>Start with</dt><dd>{guideSession.startWith}</dd></div>
                  <div><dt>You will leave with</dt><dd>{session.outcome}</dd></div>
                </dl>
              </header>

              <aside className="lhp-session-stop"><Flag aria-hidden="true" /><p><strong>Human stop rule</strong>{guideSession.stopRule}</p></aside>

              <div className="lhp-steps">
                {guideSession.steps.map((step, index) => (
                  <GuideStep
                    key={step.id}
                    step={step}
                    number={index + 1}
                    slidesById={slidesById}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />
                ))}
              </div>

              <section className="lhp-decision" aria-labelledby={`session-${session.id}-decision`}>
                <p>Decision point</p>
                <h3 id={`session-${session.id}-decision`}>{guideSession.decisionQuestion}</h3>
                <div>
                  <span><CheckCircle2 aria-hidden="true" /> Yes: carry the artifact into {session.id === 5 ? "the next run" : "the next capability"}.</span>
                  <span><RotateCcw aria-hidden="true" /> Not yet: return to the failed check and change one thing.</span>
                </div>
              </section>

              {session.id === 1 && (
                <DeepDiveChapter
                  id="source-grounded-artifact-lab"
                  eyebrow="Optional steps 01A"
                  title="Turn one approved file into a finished, reusable artifact."
                  summary="Open the source-to-artifact lab"
                >
                  <div className="lhp-lab-step-list">
                    {FOUNDATION_STEPS.map((step, index) => (
                      <MegaGuideStepCard key={step.id} step={step} number={index + 1} copiedId={copiedId} onCopy={handleCopy} />
                    ))}
                  </div>
                  <p className="lhp-chapter-bridge"><ArrowRight aria-hidden="true" /><span><strong>Carry forward</strong>Save the source set, the finished artifact, and the verification notes inside the same Chief of Staff workspace.</span></p>
                </DeepDiveChapter>
              )}

              {session.id === 2 && (
                <DeepDiveChapter
                  id="decision-lab"
                  eyebrow="Optional steps 02A"
                  title="Rank the Job, define the result, and record the decision."
                  summary="Open the RICE → TDAR → DMP steps"
                >
                  <CourseLab course={ricePrioritiesCourse} copiedId={copiedId} onCopy={handleCopy} />
                  <CourseLab course={tdarResultsCourse} copiedId={copiedId} onCopy={handleCopy} />
                  <CourseLab course={decisionMakingCourse} copiedId={copiedId} onCopy={handleCopy} />
                </DeepDiveChapter>
              )}

              {session.id === 3 && (
                <DeepDiveChapter
                  id="platform-paths"
                  eyebrow="Optional steps 03A"
                  title="Configure the tool path you selected."
                  summary="Choose Claude, Microsoft 365, or Builder steps"
                >
                  <PlatformPaths />
                </DeepDiveChapter>
              )}

              {session.id === 4 && (
                <DeepDiveChapter
                  id="advanced-agent-work"
                  eyebrow="Optional steps 04A"
                  title="Build one safe slice or hand work between two agents."
                  summary="Open the build and agent-handoff steps"
                >
                  <BuilderPath />
                  <AgentHandoffLab />
                </DeepDiveChapter>
              )}

              {session.id === 5 && (
                <>
                  <DeepDiveChapter
                    id="skill-forge-lab"
                    eyebrow="Optional steps 05A"
                    title="Package the workflow as a reusable Skill."
                    summary="Open the Skill Forge steps"
                  >
                    <div className="lhp-lab-step-list">
                      {SKILL_FORGE_STEPS.map((step, index) => (
                        <MegaGuideStepCard key={step.id} step={step} number={index + 1} copiedId={copiedId} onCopy={handleCopy} />
                      ))}
                    </div>
                    <p className="lhp-chapter-bridge"><ArrowRight aria-hidden="true" /><span><strong>Carry forward</strong>Version the Skill with the test cases, failed examples, metric definition, and named owner from the Chief of Staff loop.</span></p>
                  </DeepDiveChapter>

                  <DeepDiveChapter
                    id="safety-governance"
                    eyebrow="Optional steps 06"
                    title="Complete the Safety & Governance record."
                    summary="Open the Safety & Governance steps"
                  >
                    <CourseLab course={responsibleAIPracticeCourse} copiedId={copiedId} onCopy={handleCopy} />
                    <div className="lhp-governance-links">
                      <ShieldCheck aria-hidden="true" />
                      <div><h3>Check the optional organizational controls.</h3><p>Rate each Safety & Governance item, save the missing evidence, and assign an owner for every red or yellow control.</p></div>
                      <a href="/ai-30/program#ai-tune-up">Open Safety & Governance <ArrowRight aria-hidden="true" /></a>
                    </div>
                  </DeepDiveChapter>
                </>
              )}
            </section>
          );
        })}

        <EvidencePortfolio />

        <section className="lhp-library" id="use-case-library-workflows" aria-labelledby="library-title">
          <span id="use-case-library-scale" className="lhp-alias-anchor" aria-hidden="true" />
          <div className="lhp-section-intro">
            <p className="lhp-eyebrow">Optional examples</p>
            <h2 id="library-title">Try another use case after the current one passes.</h2>
            <p>Start with the Chief of Staff or AI Watcher. Open this list only when you need another pattern.</p>
          </div>
          <details>
            <summary><span>Browse {S2P_ELEVATE_PROGRAM.showcase.length} additional patterns</span><ChevronDown aria-hidden="true" /></summary>
            <div className="lhp-library-list">
              {S2P_ELEVATE_PROGRAM.showcase.map((item, index) => (
                <article key={item.id}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div><p>{item.category} · {item.platform}</p><h3>{toAtlasLanguage(item.title)}</h3><p>{toAtlasLanguage(item.lead)}</p></div>
                </article>
              ))}
            </div>
          </details>
          <a className="lhp-library-cta" href="/library">Get the complete use-case and prompt library <ArrowRight aria-hidden="true" /></a>
        </section>

        <section className="lhp-finish" aria-labelledby="finish-title">
          <p className="lhp-eyebrow">Next step</p>
          <h2 id="finish-title">Retake the AI Tune-Up.</h2>
          <p>Change a rating only when you can repeat the capability and show the saved evidence.</p>
          <div>
            <a href="/ai-30/program#ai-tune-up">Retake the AI Tune-Up <ArrowRight aria-hidden="true" /></a>
            <a href="/ai-30/program">Return to the roadmap</a>
          </div>
        </section>
      </main>

      <footer className="lhp-footer">
        <a href="/ai-30/program" className="lhp-brand"><img src="/orange-logo.png" alt="Tech Leaders" width="170" height="38" /></a>
        <p>Longhand AI · Practical AI capability with visible human accountability.</p>
      </footer>
    </div>
  );
};

export default LonghandAIProgram;
